import { useState } from "react";
import { X, MessageCircle, Send, Bookmark, MoreHorizontal, BadgeCheck, BookOpen, ArrowLeft, Share2, Copy, Check } from "lucide-react";
import { Link } from "react-router-dom";

const ARTICLE_IMAGE = "/images/article-sweat.jpg";

export const FeaturedArticleModal = ({ isOpen, onClose }) => {
  const [saved, setSaved] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!isOpen) return null;

  const shareUrl = typeof window !== "undefined" ? window.location.origin + "/" : "https://deo.co.il";
  const shareText = "הידעתם? לזיעה שלכם אין ריח. בכלל. גלו את המדע מאחורי ריח הגוף, ולמה סודה לשתייה היא הפתרון הטבעי.";
  const shareTitle = "M'art, דאודורנט טבעי";

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title: shareTitle, text: shareText, url: shareUrl }); }
      catch (e) { /* user cancelled */ }
    } else {
      setShareMenuOpen(v => !v);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* noop */ }
  };

  const enc = encodeURIComponent;
  const shareLinks = [
    { name: "WhatsApp", href: `https://wa.me/?text=${enc(shareText + " " + shareUrl)}`, color: "#25D366" },
    { name: "Telegram", href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}`, color: "#229ED9" },
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, color: "#1877F2" },
    { name: "X", href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`, color: "#1d231b" },
  ];

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="featured-article-modal"
      dir="rtl"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#1d231b]/60 backdrop-blur-sm"
        style={{ animation: "fa-fade 0.25s ease-out" }}
        onClick={onClose}
        data-testid="featured-article-backdrop"
      />

      {/* Social Post Card */}
      <div
        className="relative bg-white w-full sm:max-w-[470px] max-h-[94vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl shadow-2xl"
        style={{ animation: "fa-rise 0.4s cubic-bezier(0.16,1,0.3,1)" }}
      >
        {/* ===== Post header (sticky) ===== */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-white/95 backdrop-blur-md px-3.5 py-3 border-b border-[#EFEAE2]">
          <div className="flex items-center gap-2.5">
            {/* Gradient ring avatar */}
            <div className="p-[2px] rounded-full bg-gradient-to-tr from-[#C47F64] via-[#D9A38C] to-[#4A5D43]">
              <div className="w-9 h-9 rounded-full bg-white p-[2px]">
                <div className="w-full h-full rounded-full bg-[#2C3329] flex items-center justify-center">
                  <span className="font-serif text-white text-sm leading-none">M</span>
                </div>
              </div>
            </div>
            <div className="leading-tight">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-[13px] text-[#1d231b]">M'art Posts</span>
                <BadgeCheck className="h-3.5 w-3.5 text-[#3897F0] fill-[#3897F0]/15" />
              </div>
              <span className="text-[11px] text-[#9A9790]">מקודם · מאמר מערכת</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <MoreHorizontal className="h-5 w-5 text-[#6B7266]" />
            <button
              onClick={onClose}
              data-testid="featured-article-close-top"
              className="p-1.5 rounded-full text-[#6B7266] hover:bg-[#F5F2EB] transition-colors"
              aria-label="סגירה"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ===== Post media ===== */}
        <div className="relative w-full aspect-square sm:aspect-[4/5] overflow-hidden bg-[#F5F2EB]">
          <img src={ARTICLE_IMAGE} alt="המדע מאחורי ריח הזיעה" className="w-full h-full object-cover" />
          <div className="absolute top-3 left-3 bg-black/45 backdrop-blur-sm text-white text-[11px] font-medium px-2.5 py-1 rounded-full">
            מדע · טבע · גוף
          </div>
        </div>

        {/* ===== Action bar ===== */}
        <div className="px-3.5 pt-3 pb-1.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={handleShare} data-testid="article-share-icon" aria-label="שיתוף" className="transition-transform active:scale-90">
                <Send className="h-6 w-6 text-[#1d231b]" style={{ transform: "scaleX(-1)" }} />
              </button>
              <MessageCircle className="h-6 w-6 text-[#1d231b]" style={{ transform: "scaleX(-1)" }} />
            </div>
            <button onClick={() => setSaved(v => !v)} aria-label="שמירה" className="transition-transform active:scale-90">
              <Bookmark className={`h-6 w-6 transition-colors ${saved ? "fill-[#1d231b] text-[#1d231b]" : "text-[#1d231b]"}`} />
            </button>
          </div>
          {/* Social proof: shares */}
          <div className="flex items-center gap-2 mt-2.5">
            <div className="flex flex-row-reverse">
              {["#C47F64", "#4A5D43", "#7B8C96"].map((c, i) => (
                <span key={i} className="w-5 h-5 rounded-full border-2 border-white -ml-2 first:ml-0" style={{ background: c }} />
              ))}
            </div>
            <p className="text-[13px] font-semibold text-[#1d231b]">10+ משתמשים שיתפו את המאמר</p>
          </div>
        </div>

        {/* ===== Caption / article body ===== */}
        <div className="px-3.5 pb-2 text-[14px] leading-relaxed text-[#262219]">
          <p className="font-serif text-lg text-[#1d231b] mb-2 leading-snug">
            <span className="font-semibold">mart.posts</span> · הידעתם? לזיעה שלכם אין ריח. בכלל. 🤯
          </p>

          <div className="space-y-3">
            <p>
              כן, קראתם נכון. הנוזל שהגוף מפריש כשחם בחוץ או כשמתאמצים מורכב מ-99% מים וקצת מלחים.
              הוא שקוף, נקי, ולגמרי חסר ריח.
            </p>
            <p>
              אז מאיפה מגיע הריח המוכר (והפחות אהוב)? מהשותפים הסמויים שלנו: החיידקים 🦠
              ברגע שהזיעה מגיעה אל העור, הם מפרקים את החלבונים והשומנים שבה, ותוצרי הפירוק הם אלה שיוצרים את הריח.
            </p>

            {/* Highlight stats, styled like a saved highlights strip */}
            <div className="grid grid-cols-3 gap-2 py-1">
              {[
                { v: "99%", l: "מהזיעה: מים ומלחים" },
                { v: "10K-1M", l: "חיידקים במ\"מ אחד" },
                { v: "2", l: "סוגי בלוטות זיעה" },
              ].map((s, i) => (
                <div key={i} className="bg-[#FAF8F5] border border-[#EFEAE2] rounded-xl px-2 py-3 text-center">
                  <p className="font-serif text-[15px] text-[#4A5D43] font-semibold">{s.v}</p>
                  <p className="text-[10.5px] text-[#7A7A6E] leading-tight mt-0.5">{s.l}</p>
                </div>
              ))}
            </div>

            <p>
              אז איך מנצחים את הריח בלי לחסום את הגוף? הרבה דאודורנטים מסחריים סותמים פיזית את בלוטות הזיעה עם אלומיניום.
              אבל להזיע זה בריא: זה מנגנון קירור וניקוי רעלים חיוני.
            </p>
            <p className="font-semibold text-[#1d231b]">
              הפתרון האמיתי הוא לא לעצור את הזיעה, אלא לשנות את הסביבה של החיידקים.
            </p>

            <div className="bg-[#4A5D43]/[0.06] rounded-xl p-3.5 border border-[#4A5D43]/15">
              <p className="font-semibold text-[#1d231b] mb-2">וכאן נכנסת סודה לשתייה באיכות קוסמטית ✨</p>
              <p className="mb-2.5 text-[13.5px]">
                מחקרים מדעיים (כמו אלו שבמאגר PubMed העולמי) מראים שלסודיום ביקרבונט תכונות אנטי-מיקרוביאליות עוצמתיות.
                היא עושה שני דברים במקביל:
              </p>
              <p className="text-[13.5px] mb-1.5">✔️ משנה את החומציות (pH) לסביבה בסיסית עדינה, שמשבשת לחיידקים את חילוף החומרים ומונעת מהם להתרבות.</p>
              <p className="text-[13.5px]">✔️ מבטלת את הריח מהשורש, במקום להסוות אותו בבושם חזק.</p>
            </div>

            <p>
              עם סודה לשתייה טחונה דק באיכות קוסמטית מקבלים הגנה טבעית לחלוטין: הגוף נושם ומזיע כרגיל, ואתם נשארים רעננים ונקיים מריחות לאורך כל היום. 🌱
            </p>
            <p className="font-serif text-base text-[#4A5D43] text-center py-1">
              מזיעים? מעולה. מריחים? ממש לא חובה.
            </p>
            <p className="text-[#3897F0] font-medium text-[13px]">
              #דאודורנט_טבעי #ללא_אלומיניום #סודה_לשתייה #טיפוח_טבעי #Mart #קוד_פתוח
            </p>
          </div>

          {/* Comments teaser */}
          <p className="text-[13px] text-[#9A9790] mt-3">הצגת כל 92 התגובות</p>
          <p className="text-[11px] text-[#B5B2A8] mt-1 uppercase tracking-wide">לפני יומיים</p>
        </div>

        {/* ===== Share menu (fallback when native share unavailable) ===== */}
        {shareMenuOpen && (
          <div className="px-3.5 pb-1" data-testid="article-share-menu">
            <div className="flex flex-wrap gap-2">
              {shareLinks.map(s => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-[68px] text-center text-white text-xs font-semibold rounded-full py-2.5"
                  style={{ background: s.color }}
                  data-testid={`share-${s.name.toLowerCase()}`}
                >
                  {s.name}
                </a>
              ))}
              <button
                onClick={copyLink}
                data-testid="share-copy-link"
                className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 border border-[#EFEAE2] text-[#4A5D43] text-xs font-semibold rounded-full py-2.5"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "הקישור הועתק" : "העתקת קישור"}
              </button>
            </div>
          </div>
        )}

        {/* ===== Footer CTAs ===== */}
        <div className="px-3.5 pt-3 pb-4 mt-1 border-t border-[#EFEAE2] flex flex-col sm:flex-row gap-2.5">
          <button
            onClick={handleShare}
            data-testid="featured-article-share-button"
            className="flex-1 flex items-center justify-center gap-2 bg-[#C47F64] hover:bg-[#b06f55] text-white rounded-full px-5 py-3 text-sm font-medium transition-colors"
          >
            <Share2 className="h-4 w-4" />
            שתף את המאמר
          </button>
          <Link to="/articles" onClick={onClose} className="flex-1" data-testid="featured-article-all-link">
            <button className="w-full flex items-center justify-center gap-2 bg-[#4A5D43] hover:bg-[#3A4A35] text-white rounded-full px-5 py-3 text-sm font-medium transition-colors">
              <BookOpen className="h-4 w-4" />
              לכל המאמרים
              <ArrowLeft className="h-4 w-4" />
            </button>
          </Link>
          <button
            onClick={onClose}
            data-testid="featured-article-close-bottom"
            className="flex items-center justify-center gap-2 border border-[#EFEAE2] text-[#6B7266] hover:bg-[#F5F2EB] rounded-full px-5 py-3 text-sm font-medium transition-colors"
          >
            <X className="h-4 w-4" />
            סגירה
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fa-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fa-rise { from { opacity: 0; transform: translateY(28px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
    </div>
  );
};

export default FeaturedArticleModal;
